import { useState, useEffect } from 'react'

import { determineTopicState } from 'app/PelatihanMandiriPage/helper'
import { useGetTopicsProgress } from 'api/microlearning/useAPIProgress'
import { useGetTopicList } from 'api/microlearning/useAPITopics'

import type { CommonListResponse } from 'types/api'
import type { Topic, OverallProgress } from 'types/microlearning'

type FetchStatus =
  | 'idle'
  | 'fetching_topics'
  | 'topics_fetched'
  | 'fetching_progress'
  | 'success'
  | 'error_fetching_topics'
  | 'error_fetching_progress'

const useTopicWithProgression = (): {
  topicWithProgress: Topic[]
  fetchStatus: FetchStatus
  progressData: CommonListResponse<OverallProgress>
} => {
  const [fetchStatus, setFetchStatus] = useState<FetchStatus>('idle')
  const [topicWithProgress, setTopicWithProgress] = useState<Topic[] | []>([])
  const { data: topicsData, status: topicStatus } = useGetTopicList()
  const { data: progressData, status: progressStatus } = useGetTopicsProgress(
    topicStatus === 'success' && topicsData?.data?.length > 0
  )

  /**
   * Let's define the cycle for fetching topics and its progress
   * the default FetchStatus is 'idle'
   * first we start by fetching the list of the topics,
   * change the FetchStatus to fetching_topics
   * when failed, set FetchStatus to error_fetching_topics - and STOP
   * when it is succeed, change the FetchStatus to topics_fetched
   * if current user is logged in (determined by "session" property)
   * then start fetching progress
   * when failed, set FetchStatus to error_fetching_progress
   */
  useEffect(() => {
    switch (topicStatus) {
      case 'loading':
        setFetchStatus('fetching_topics')
        break
      case 'error':
        setFetchStatus('error_fetching_topics')
        break
      case 'success':
        setFetchStatus(() =>
          topicsData.data.length > 0 ? 'topics_fetched' : 'success'
        )
        break
    }
  }, [topicStatus, topicsData])

  /**
   * Since topics progress only fetched when user is loggedin and topic fetch
   * is successfully done, so we can safely assumed that the default state of the
   * fetch status at this point is always fetching progress
   */
  useEffect(() => {
    switch (progressStatus) {
      default:
      case 'loading':
        setFetchStatus('fetching_progress')
        break
      case 'success':
        setFetchStatus('success')
        break
      case 'error':
        setFetchStatus('error_fetching_progress')
        break
    }
  }, [progressStatus])

  /**
   * When the overall fetch status is completed
   * now we can safely consolidating the progression data with the topics data
   */
  useEffect(() => {
    if (fetchStatus === 'success') {
      //get each topic progression status from the progression data
      let topicsWithProgress: Topic[] = []
      topicsData?.data?.forEach((topic) => {
        if (topic.totalModule > 0) {
          let currentTopicProgress = progressData?.data?.find((progress) => {
            return topic.id === progress.id
          })
          //determine the text for the badge
          const topicProgressState = determineTopicState(currentTopicProgress)
          switch (topicProgressState) {
            default:
              topic.badge = undefined
              break
            case 'MODULE_ON_PROGRESS':
              topic.badge = {
                type: 'informational',
                text: 'Pelajari Modul',
              }
              break
            case 'MODULE_DONE':
            case 'AKSI_NYATA_CREATED':
            case 'AKSI_NYATA_ON_PROGRESS':
              topic.badge = {
                type: 'informational',
                text: 'Praktikkan Aksi Nyata',
                state: 'ongoing',
              }
              break
            case 'AKSI_NYATA_NEED_REVISION':
              topic.badge = {
                type: 'warning',
                text: 'Butuh perbaikan Aksi Nyata',
                state: 'ongoing',
              }
              break
            case 'AKSI_NYATA_DONE':
              topic.badge = {
                type: 'neutral',
                text: 'Menunggu hasil validasi',
                state: 'completed',
              }
              break
            case 'AKSI_NYATA_APPEAL':
              topic.badge = {
                type: 'neutral',
                text: 'Menunggu hasil banding',
                state: 'completed',
              }
              break
            case 'AKSI_NYATA_VALIDATED':
              if (!topic.certifiable) {
                topic.badge = {
                  type: 'neutral',
                  text: 'Menunggu hasil validasi',
                  state: 'completed',
                }
                break
              }
              topic.badge = {
                type: 'success',
                text: 'Sertifikat tersedia',
                state: 'validated',
              }
              break
          }
          topicsWithProgress.push(topic)
        }
      })
      setTopicWithProgress(topicsWithProgress)
    }
  }, [fetchStatus, progressData?.data, topicsData?.data])

  return { topicWithProgress, fetchStatus, progressData }
}

export default useTopicWithProgression
